import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Nodes & Clients',
    tagline: 'The split personality every Ethereum node has had since the Merge.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what the execution client vs. consensus client are each responsible for
//   Q1 - why trusting a third-party RPC provider is a real security boundary
export const blocks: StageBlock[] = [
    content('two-clients', () => (
        <StageSection gradientBorder eyebrow="Stage 4 of 8" title="Since the Merge, every node is really two programs">
            <Prose>
                Before September 2022, one piece of software did everything. After Ethereum's Merge to proof of
                stake, running a full node means running two separate clients that talk to each other over a local
                API:
            </Prose>
            <FactGrid items={[
                ['Execution client', 'Runs the EVM, executes transactions, maintains state and the mempool. Examples: Geth, Nethermind, Besu, Erigon.'],
                ['Consensus client', 'Runs the proof-of-stake protocol, proposing and attesting to blocks, tracking validators. Examples: Prysm, Lighthouse, Teku, Nimbus.'],
            ]} />
            <Prose>
                The consensus client tells the execution client which block to build or validate next; the
                execution client reports back the resulting state root. Neither one is a complete Ethereum node on
                its own anymore.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('client-diversity', () => (
        <StageSection title="Why client diversity is a stated goal">
            <Prose>
                If every validator ran the exact same execution client and that client had a consensus-breaking bug,
                the entire network could fork on a bad state transition. Running a healthy mix of independently
                implemented clients means a bug in one of them affects only its share of the network rather than
                everyone at once, which is why client teams and the community actively track and try to reduce any
                single client's dominance.
            </Prose>
        </StageSection>
    )),

    content('rpc', () => (
        <StageSection title="JSON-RPC: how everything else talks to a node">
            <Prose>
                Wallets, dapp frontends, and this platform's own backend don't parse blockchain data directly; they
                send JSON-RPC requests (<code>eth_getBalance</code>, <code>eth_call</code>,{' '}
                <code>eth_sendRawTransaction</code>, and dozens more) to a node's RPC endpoint and get JSON back.
            </Prose>
            <KeyPoints items={[
                <>Running your own node gives you a trustless view of the chain: you verify everything yourself.</>,
                <>Using someone else's RPC endpoint (a hosted provider, a public gateway) means trusting that endpoint to report state honestly and not censor or reorder your requests.</>,
                <>A wallet that silently switches RPC providers, or a dapp that points at an untrusted endpoint, can be shown fabricated balances or be tricked into signing against stale state. The trust boundary is real, not theoretical.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
